import * as React from 'react';

export interface StepProps {
  id?: string;
  when?: () => boolean;
  children: React.ReactElement;
}

export const Step: React.FC<StepProps> = () => null;

type RegisteredStep = {
  id: string;
  order: number;
  qualified: () => boolean;
  element: React.ReactElement;
};

type WizardNav = {
  history: string[];
  cursor: number;
};

export type WizardControls = {
  prev: () => void;
  next: () => void;
  canPrev: boolean;
  canNext: boolean;
  cursor: number;
  history: string[];
  current: RegisteredStep | null;
};

export type WizardProps = {
  children: React.ReactNode;
  onFinish?: () => void;
  autoStart?: boolean;
};

const WizardContext = React.createContext<WizardControls | null>(null);

export function useWizard(): WizardControls {
  const ctx = React.useContext(WizardContext);
  if (!ctx) {
    throw new Error('useWizard must be used inside <Wizard>.');
  }
  return ctx;
}

function collectStepElements(node: React.ReactNode): Array<React.ReactElement<StepProps>> {
  const collected: Array<React.ReactElement<StepProps>> = [];
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) {
      return;
    }
    if (child.type === Step) {
      collected.push(child as React.ReactElement<StepProps>);
      return;
    }
    if (child.props?.children) {
      collected.push(...collectStepElements(child.props.children));
    }
  });
  return collected;
}

export const Wizard: React.FC<WizardProps> = ({ children, onFinish, autoStart = true }) => {
  const stepElements = React.useMemo(() => {
    return collectStepElements(children);
  }, [children]);

  const steps = React.useMemo<RegisteredStep[]>(() => {
    return stepElements.map((el, index) => {
      const { id, when, children: stepChild } = el.props;
      if (!React.isValidElement(stepChild)) {
        throw new Error(`Step #${index}: child must be a valid React element.`);
      }
      return {
        id,
        order: index,
        qualified: when ?? (() => true),
        element: stepChild,
      };
    });
  }, [stepElements]);

  const [nav, setNav] = React.useState<WizardNav>({ history: [], cursor: -1 });

  const findStepById = React.useCallback(
    (id: string) => {
      const step = steps.find((x) => x.id === id) ?? null;
      return step;
    },
    [steps],
  );

  const current = React.useMemo<RegisteredStep | null>(() => {
    if (nav.cursor < 0 || nav.cursor >= nav.history.length) {
      return null;
    }
    return findStepById(nav.history[nav.cursor]);
  }, [nav.cursor, nav.history, findStepById]);

  const findFirstQualifiedStep = React.useCallback(() => {
    const step = steps.find((x) => x.qualified()) ?? null;
    return step;
  }, [steps]);

  const findNextUnseenQualified = React.useCallback(
    (afterOrder: number, history: string[]) => {
      const step = steps
        .slice(afterOrder + 1)
        .find((x) => x.qualified() && !history.includes(x.id));
      return step ?? null;
    },
    [steps],
  );

  React.useEffect(() => {
    if (!autoStart || nav.cursor !== -1) {
      return;
    }
    const firstStep = findFirstQualifiedStep();
    if (firstStep) {
      setNav({ history: [firstStep.id], cursor: 0 });
      return;
    }
    if (onFinish) {
      onFinish();
    }
  }, [autoStart, nav.cursor, findFirstQualifiedStep, onFinish]);

  const next = React.useCallback(() => {
    setNav((prevState) => {
      if (prevState.history.length === 0) {
        const firstStep = findFirstQualifiedStep();
        if (firstStep) {
          return { history: [firstStep.id], cursor: 0 };
        }
        if (onFinish) {
          onFinish();
        }
        return prevState;
      }

      if (prevState.cursor < prevState.history.length - 1) {
        return { ...prevState, cursor: prevState.cursor + 1 };
      }

      const lastVisitedStep = findStepById(prevState.history[prevState.history.length - 1]);
      const nextQualifiedStep = findNextUnseenQualified(
        lastVisitedStep ? lastVisitedStep.order : -1,
        prevState.history,
      );

      if (nextQualifiedStep) {
        return {
          history: [...prevState.history, nextQualifiedStep.id],
          cursor: prevState.history.length,
        };
      }

      if (onFinish) {
        onFinish();
      }
      return prevState;
    });
  }, [findFirstQualifiedStep, findStepById, findNextUnseenQualified, onFinish]);

  const canPrev = nav.cursor > 0;

  const prev = React.useCallback(() => {
    setNav((prevState) => {
      if (prevState.cursor <= 0) {
        // schon am Anfang – nichts zu tun
        return prevState;
      }

      const newCursor = prevState.cursor - 1;
      // Tail der History kappen, damit „Zukunft“ vergessen wird
      const newHistory = prevState.history.slice(0, newCursor + 1);

      return {
        history: newHistory,
        cursor: newCursor,
      };
    });
  }, []);

  const canNext = React.useMemo(() => {
    if (nav.cursor >= 0 && nav.cursor < nav.history.length - 1) {
      return true;
    }
    if (nav.history.length === 0) {
      return !!findFirstQualifiedStep();
    }
    const lastVisitedStep = findStepById(nav.history[nav.history.length - 1]);
    return !!findNextUnseenQualified(lastVisitedStep ? lastVisitedStep.order : -1, nav.history);
  }, [nav.cursor, nav.history, findFirstQualifiedStep, findStepById, findNextUnseenQualified]);

  // needed to get stable callbacks for prev and next, to get the correct step content
  function useStableCallback<T extends(...args: unknown[]) => unknown>(fn: T): T {
    const ref = React.useRef<T>(fn);
    React.useLayoutEffect(() => {
      ref.current = fn;
    }, [fn]);

    // ref identity is stable, so empty deps are correct here
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return React.useCallback(((...args: Parameters<T>) => ref.current(...args)) as T, []);
  }
  const prevStable = useStableCallback(prev);
  const nextStable = useStableCallback(next);

  const value = React.useMemo<WizardControls>(() => {
    return {
      prev: prevStable,
      next: nextStable,
      canPrev,
      canNext,
      cursor: nav.cursor,
      history: nav.history,
      current,
    };
  }, [prevStable, nextStable, canPrev, canNext, nav.cursor, nav.history, current]);

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
};

// using current step in component to show step content
export const WizardCurrent: React.FC = () => {
  const { current } = useWizard();
  return React.useMemo(() => {
    if (!current) {
      return null;
    }

    return React.cloneElement(current.element, { key: current.id });
  }, [current]);
};
